import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, InfoOutlined, Visibility } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { type Dayjs } from "dayjs";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DataTable } from "@/components/DataTable";
import { FilterModal } from "@/components/FilterModal";
import { CrudModal } from "@/components/crud/CrudModal";
import usePagination from "@/hooks/pagination";
import { useCrud } from "@/hooks/useCrud";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import {
  calcularCenario as calcularCenarioApi,
  deleteCenario,
  listCenarios,
  listDistribuicoesArea,
  MAX_PERIODOS_CENARIO,
  saveCenario,
  statusCalculo as statusCalculoApi,
  type Cenario as CenarioApi,
  type DistribuicaoArea,
  type StatusCenario,
} from "@/api/metas.service";
import { MetasParametrosModal } from "./MetasParametrosModal";
import { corSituacao, situacaoDe, statusCalculoDe } from "./metasStatus";
import { CHAVE_MES, chaveDe, mesesPermitidos, normalizar, paraEscala, partesDe, rotuloMesChave } from "./metasFormato";

// Cenário de cálculo da meta: combina os meses de referência (com o peso de
// cada um) e o crescimento esperado das vendas. Mesmo formato que o motor de
// cálculo do Flash (PKG_FLASH_META_CALC, no banco de cada cliente) consome —
// só 1 a 3 períodos históricos, crescimento sempre percentual, companhia
// inteira. O cenário é persistido pelo Product Runtime (metas.list_cenarios /
// save_cenario / delete_cenario); o formulário trabalha com "AAAA-MM" e
// strings, e os conversores logo abaixo traduzem isso para o formato
// ano/mês/número do back-end.

/** Peso de cada mês de referência, indexado por "AAAA-MM". */
type Impactos = Record<string, string>;

interface Cenario {
  id: string;
  descricao: string;
  mesMeta: string;
  impactos: Impactos;
  crescimento: string;
  propagarPai: boolean;
  /** Percentual de vendas esperado em cada quinzena do mês da meta (somam 100%). */
  quinzena1: string;
  quinzena2: string;
  /** Distribuição de Área escolhida; vazio = nenhuma. */
  distribuicaoAreaId: string;
  status: StatusCenario;
  /** Por que a última tentativa de cálculo falhou — sobrevive ao recarregar a tela, já que vem da listagem. */
  erroCalculo?: string;
}

type Rascunho = Omit<Cenario, "id" | "status" | "erroCalculo">;

/** "AAAA-MM" para o objeto que o DatePicker espera, ou null se vazio/inválido. */
function paraDayjs(chave: string): Dayjs | null {
  if (!CHAVE_MES.test(chave)) return null;
  const { ano, mes } = partesDe(chave);
  return dayjs(new Date(ano, mes - 1, 1));
}

/** O que o DatePicker devolve, de volta para "AAAA-MM". */
function deDayjs(data: Dayjs | null) {
  return data && data.isValid() ? chaveDe(data.year(), data.month() + 1) : "";
}

function mesAtual() {
  const hoje = new Date();
  return chaveDe(hoje.getFullYear(), hoje.getMonth() + 1);
}

/** Descarta pesos de meses que não são mais um período histórico permitido. */
function comPermitidos(impactos: Impactos): Impactos {
  const permitidos = new Set(mesesPermitidos());
  return Object.fromEntries(Object.entries(impactos).filter(([mes]) => permitidos.has(mes)));
}

/** Peso de um mês de referência: três casas decimais, sempre positivo (metas_cenario_periodos.peso é NUMERIC(6,3)). */
function milesimos(valor: string) {
  return paraEscala(valor, 3);
}

function totalDe(impactos: Impactos) {
  const valores = Object.values(impactos);
  const soma = valores.reduce((acc, v) => acc + (milesimos(v) ?? 0), 0);
  const validos = valores.every((v) => {
    const m = milesimos(v);
    return m !== null && m > 0 && m <= 100000;
  });
  return { soma, validos, quantidade: valores.length, ok: valores.length > 0 && validos && soma === 100000 };
}

/**
 * Percentual de uma quinzena: duas casas decimais (metas_cenarios.perc_*_quinzena é NUMERIC(5,2)),
 * devolvido em milésimos para somar e formatar como os pesos.
 */
function milesimosQuinzena(valor: string) {
  const centesimos = paraEscala(valor, 2);
  return centesimos === null ? null : centesimos * 10;
}

function totalQuinzenasDe(quinzena1: string, quinzena2: string) {
  const valores = [milesimosQuinzena(quinzena1), milesimosQuinzena(quinzena2)];
  const soma = valores.reduce<number>((acc, v) => acc + (v ?? 0), 0);
  const validos = valores.every((v) => v !== null && v >= 0 && v <= 100000);
  return { soma, validos, ok: validos && soma === 100000 };
}

function dicaQuinzenas(total: ReturnType<typeof totalQuinzenasDe>) {
  if (!total.validos) return "Informe um percentual válido para cada quinzena.";
  if (total.ok) return "Quinzenas somam 100%.";
  return total.soma < 100000
    ? `Faltam ${formatarPercentual(100000 - total.soma)}% para completar 100%.`
    : `O total excede 100% em ${formatarPercentual(total.soma - 100000)}%.`;
}

function formatarPercentual(valor: number) {
  return (valor / 1000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function textoCrescimento(valor: string) {
  const numero = Number(String(valor).replace(",", "."));
  if (!Number.isFinite(numero)) return "0%";
  return `${numero > 0 ? "+" : ""}${numero.toLocaleString("pt-BR")}%`;
}

/** RASCUNHO/ENVIADO/CALCULADO aceitam editar ou calcular de novo — editar um CALCULADO o devolve para RASCUNHO; só PROMOVIDO e CALCULANDO ficam travados (save_cenario/calcular_cenario, back-end). */
function editavel(status: StatusCenario) {
  return status !== "PROMOVIDO" && status !== "PROMOVENDO" && status !== "CALCULANDO";
}

/** PROMOVIDO já foi mesclado nas metas efetivas do cliente e CALCULANDO tem um motor rodando contra ele; delete_cenario recusa os dois (back-end). */
function excluivel(status: StatusCenario) {
  return status !== "PROMOVIDO" && status !== "PROMOVENDO" && status !== "CALCULANDO";
}

/**
 * O cenário está calculando tanto no intervalo entre o clique e a resposta do
 * disparo (`processando`) quanto depois dela, enquanto o motor roda de fato
 * (status CALCULANDO) — inclusive se a tela foi recarregada no meio.
 */
function emCalculo(cenario: Cenario, processando: { id: string; acao: "calcular" } | null) {
  return cenario.status === "CALCULANDO" || (processando?.id === cenario.id && processando.acao === "calcular");
}

function rascunhoVazio(impactos: Impactos): Rascunho {
  return { descricao: "", mesMeta: mesAtual(), impactos, crescimento: "0", propagarPai: true, quinzena1: "50", quinzena2: "50", distribuicaoAreaId: "" };
}

/** Situação exibida na listagem, na mesma granularidade da coluna Situação — RASCUNHO e ENVIADO contam como um único filtro. */
type SituacaoFiltro = "NAO_CALCULADO" | "CALCULANDO" | "CALCULADO" | "PROMOVIDO";

const OPCOES_SITUACAO: { value: SituacaoFiltro; label: string }[] = [
  { value: "NAO_CALCULADO", label: "Não calculado" },
  { value: "CALCULANDO", label: "Calculando" },
  { value: "CALCULADO", label: "Calculado" },
  { value: "PROMOVIDO", label: "Publicado" },
];

function situacaoFiltroDe(status: StatusCenario): SituacaoFiltro {
  const situacao = statusCalculoDe(status);
  if (situacao.calculando) return "CALCULANDO";
  if (!situacao.calculado) return "NAO_CALCULADO";
  return situacao.publicado ? "PROMOVIDO" : "CALCULADO";
}

function validar(rascunho: Rascunho, mesMinimo: string) {
  const problemas: string[] = [];
  const total = totalDe(rascunho.impactos);
  if (!rascunho.descricao.trim()) problemas.push("Informe a descrição do cenário.");
  if (!CHAVE_MES.test(rascunho.mesMeta) || rascunho.mesMeta < mesMinimo)
    problemas.push("O mês da meta deve ser o mês atual ou um mês futuro.");
  if (!total.quantidade || total.quantidade > MAX_PERIODOS_CENARIO)
    problemas.push(`Selecione de 1 a ${MAX_PERIODOS_CENARIO} meses dentro do período permitido.`);
  if (!total.validos)
    problemas.push("Cada mês selecionado precisa de um peso positivo, com até três casas decimais.");
  if (total.soma !== 100000)
    problemas.push(
      `A soma dos pesos deve ser exatamente 100%. Total informado: ${formatarPercentual(total.soma)}%.`
    );
  const quinzenas = totalQuinzenasDe(rascunho.quinzena1, rascunho.quinzena2);
  if (!quinzenas.validos)
    problemas.push("Informe um percentual válido para cada quinzena, com até duas casas decimais.");
  else if (!quinzenas.ok)
    problemas.push(
      `A soma das quinzenas deve ser exatamente 100%. Total informado: ${formatarPercentual(quinzenas.soma)}%.`
    );
  if (paraEscala(rascunho.crescimento, 4, true) === null)
    problemas.push("Informe um crescimento válido, com até quatro casas decimais (pode ser negativo).");
  return problemas;
}

/** Converte o cenário gravado no back-end para o formato do formulário. */
function daApi(cenario: CenarioApi): Cenario {
  return {
    id: cenario.id ?? "",
    descricao: cenario.descricao,
    mesMeta: chaveDe(cenario.anoMeta, cenario.mesMeta),
    impactos: Object.fromEntries(cenario.referencias.map((r) => [chaveDe(r.ano, r.mes), String(r.peso)])),
    crescimento: String(cenario.crescimento),
    propagarPai: cenario.propagarPai,
    quinzena1: String(cenario.distribuicaoQuinzenal?.perc1aQuinzena ?? 50),
    quinzena2: String(cenario.distribuicaoQuinzenal?.perc2aQuinzena ?? 50),
    distribuicaoAreaId: cenario.distribuicaoAreaId ?? "",
    status: cenario.status ?? "RASCUNHO",
    erroCalculo: cenario.erroCalculo,
  };
}

/** Converte o formulário para o formato do back-end. */
function paraApi(rascunho: Rascunho, codSistema: string, id?: string): CenarioApi {
  const { ano, mes } = partesDe(rascunho.mesMeta);
  return {
    ...(id ? { id } : {}),
    codSistema,
    descricao: rascunho.descricao.trim(),
    anoMeta: ano,
    mesMeta: mes,
    crescimento: Number(String(rascunho.crescimento).replace(",", ".")),
    propagarPai: rascunho.propagarPai,
    distribuicaoQuinzenal: {
      perc1aQuinzena: Number(String(rascunho.quinzena1).replace(",", ".")),
      perc2aQuinzena: Number(String(rascunho.quinzena2).replace(",", ".")),
    },
    ...(rascunho.distribuicaoAreaId ? { distribuicaoAreaId: rascunho.distribuicaoAreaId } : {}),
    referencias: mesesPermitidos()
      .filter((chave) => rascunho.impactos[chave] !== undefined)
      .map((chave) => {
        const partes = partesDe(chave);
        return {
          ano: partes.ano,
          mes: partes.mes,
          peso: Number(String(rascunho.impactos[chave] ?? "").replace(",", ".")),
        };
      }),
  };
}

interface CenarioFormProps {
  values: Partial<Cenario>;
  setValues: React.Dispatch<React.SetStateAction<Partial<Cenario>>>;
  readOnly: boolean;
  erros: string[];
  onFecharErros: () => void;
  mesMinimo: string;
  impactosPadrao: Impactos;
  /** Distribuições de Área que o cenário pode escolher (as ativas, mais a já escolhida, se foi inativada depois). */
  distribuicoesArea: DistribuicaoArea[];
}

function CenarioForm({ values, setValues, readOnly, erros, onFecharErros, mesMinimo, impactosPadrao, distribuicoesArea }: CenarioFormProps) {
  const form: Rascunho = { ...rascunhoVazio(impactosPadrao), ...values };
  const patch = (mudanca: Partial<Rascunho>) => setValues((prev) => ({ ...prev, ...mudanca }));

  const meses = useMemo(mesesPermitidos, []);
  const total = totalDe(form.impactos);
  const quinzenas = totalQuinzenasDe(form.quinzena1, form.quinzena2);

  const dica = total.ok
    ? "Distribuição válida para gravação."
    : !total.quantidade
      ? "Selecione os meses de referência."
      : !total.validos
        ? "Informe um peso maior que zero para cada mês selecionado."
        : total.soma < 100000
          ? `Faltam ${formatarPercentual(100000 - total.soma)}% para completar 100%.`
          : `O total excede 100% em ${formatarPercentual(total.soma - 100000)}%.`;

  const alternarMes = (mes: string, marcado: boolean) => {
    const impactos = { ...form.impactos };
    if (marcado) impactos[mes] = "";
    else delete impactos[mes];
    patch({ impactos });
  };

  return (
    <>
      <Stack gap={2}>
        <TextField
          label="Descrição do cenário"
          required
          fullWidth
          disabled={readOnly}
          value={form.descricao}
          onChange={(e) => patch({ descricao: e.target.value })}
          placeholder="Ex.: Cenário base — crescimento de vendas"
          slotProps={{ htmlInput: { maxLength: 150 } }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "center" }}>
          <DatePicker
            label="Mês da meta"
            views={["year", "month"]}
            format="MM/YYYY"
            disabled={readOnly}
            value={paraDayjs(form.mesMeta)}
            onChange={(data) => patch({ mesMeta: deDayjs(data) })}
            minDate={paraDayjs(mesMinimo) ?? undefined}
            slotProps={{ textField: { required: true, fullWidth: true } }}
            sx={{ maxWidth: { sm: 260 } }}
          />
          <Typography variant="caption" color="text.secondary">
            Disponível a partir de {rotuloMesChave(mesMinimo)}. O mês atual e os meses futuros estão disponíveis.
          </Typography>
        </Stack>
      </Stack>

      <Stack gap={2}>
        <Typography variant="body2" color="text.secondary">
          Selecione de 1 a {MAX_PERIODOS_CENARIO} meses fechados (relativos a hoje) e informe o peso de cada um.
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, mb: 1, display: "block" }}>
          {meses.length
            ? `${rotuloMesChave(meses[meses.length - 1] ?? "")} a ${rotuloMesChave(meses[0] ?? "")}`
            : "Nenhum mês fechado disponível"}
        </Typography>

        <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">USAR</TableCell>
                <TableCell>MÊS DE REFERÊNCIA</TableCell>
                <TableCell sx={{ minWidth: 130 }}>PESO (%)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {meses.map((mes) => {
                const selecionado = form.impactos[mes] !== undefined;
                return (
                  <TableRow key={mes} sx={{ bgcolor: selecionado ? "background.subtle" : "transparent" }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selecionado}
                        disabled={
                          readOnly || (!selecionado && totalDe(form.impactos).quantidade >= MAX_PERIODOS_CENARIO)
                        }
                        onChange={(e) => alternarMes(mes, e.target.checked)}
                        slotProps={{ input: { "aria-label": `Usar ${rotuloMesChave(mes)}` } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {rotuloMesChave(mes)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="text"
                        inputMode="decimal"
                        disabled={readOnly || !selecionado}
                        value={form.impactos[mes] ?? ""}
                        placeholder="0,000"
                        onChange={(e) => {
                          const valor = e.target.value;
                          if (!/^\d*[.,]?\d{0,3}$/.test(valor)) return;
                          patch({ impactos: { ...form.impactos, [mes]: valor } });
                        }}
                        slotProps={{
                          htmlInput: {
                            "aria-label": `Peso de ${rotuloMesChave(mes)} em percentual`,
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={1}
          aria-live="polite"
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: total.ok ? "success.main" : "warning.main",
            bgcolor: (theme) => alpha(total.ok ? theme.palette.success.main : theme.palette.warning.main, 0.08),
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {total.quantidade} {total.quantidade === 1 ? "mês selecionado" : "meses selecionados"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dica}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Total{" "}
            <Typography component="span" variant="h6" sx={{ fontWeight: 800 }}>
              {formatarPercentual(total.soma)}%
            </Typography>{" "}
            / 100%
          </Typography>
        </Stack>
      </Stack>

      <Stack gap={2}>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "center" }} sx={{ mt: 1 }}>
          <TextField
            type="number"
            label="Crescimento esperado (%)"
            required
            fullWidth
            disabled={readOnly}
            value={form.crescimento}
            onChange={(e) => patch({ crescimento: e.target.value })}
            slotProps={{ htmlInput: { step: 0.0001 } }}
            sx={{ maxWidth: { sm: 260 } }}
          />
          <Typography variant="caption" color="text.secondary">
            Aplicado sobre a base ponderada dos meses de referência, para a companhia inteira. Use 0 para manter a
            base; negativo para uma retração esperada.
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          Meta = base ponderada × (1 + crescimento ÷ 100). Base ponderada = soma das vendas de cada mês
          multiplicadas pelo respectivo peso.
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Distribuição de Área
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Como a meta do mês é espalhada pelos dias. Cadastre na aba Distribuição de Área; sem escolha, o cálculo
            segue sem esse bloco.
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            label="Distribuição"
            disabled={readOnly}
            value={form.distribuicaoAreaId}
            onChange={(e) => patch({ distribuicaoAreaId: e.target.value })}
            sx={{ maxWidth: { sm: 360 } }}
          >
            <MenuItem value="">Nenhuma</MenuItem>
            {distribuicoesArea
              .filter((d) => d.ativo || d.id === form.distribuicaoAreaId)
              .map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.nome}
                  {d.ativo ? "" : " (inativa)"}
                </MenuItem>
              ))}
          </TextField>
        </Box>

        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Expectativa de venda por quinzena
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Percentual das vendas do mês da meta esperado em cada quinzena. As duas quinzenas somam 100%.
          </Typography>
          <Stack
            gap={2}
            aria-live="polite"
            sx={{
              mt: 1.5,
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: quinzenas.ok ? "success.main" : "warning.main",
              bgcolor: (theme) => alpha(quinzenas.ok ? theme.palette.success.main : theme.palette.warning.main, 0.08),
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              {(["quinzena1", "quinzena2"] as const).map((campo, i) => (
                <TextField
                  key={campo}
                  size="small"
                  type="text"
                  inputMode="decimal"
                  label={`${i + 1}ª quinzena (%)`}
                  disabled={readOnly}
                  value={form[campo]}
                  placeholder="0,00"
                  onChange={(e) => {
                    const valor = e.target.value;
                    if (!/^\d*[.,]?\d{0,2}$/.test(valor)) return;
                    patch({ [campo]: valor });
                  }}
                  sx={{ maxWidth: { sm: 200 } }}
                />
              ))}
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={1}
            >
              <Typography variant="caption" color="text.secondary">
                {dicaQuinzenas(quinzenas)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total das quinzenas:{" "}
                <Typography component="span" variant="h6" sx={{ fontWeight: 800 }}>
                  {formatarPercentual(quinzenas.soma)}%
                </Typography>{" "}
                / 100%
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Direção do cálculo
          </Typography>
          <RadioGroup
            row
            value={form.propagarPai ? "ascendente" : "descendente"}
            onChange={(e) => patch({ propagarPai: e.target.value === "ascendente" })}
          >
            <FormControlLabel
              value="ascendente"
              control={<Radio disabled={readOnly} />}
              label="Ascendente"
            />
            <FormControlLabel
              value="descendente"
              control={<Radio disabled={readOnly} />}
              label="Descendente"
            />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Ascendente também soma os ajustes MES nos níveis acima (categoria, loja, companhia); descendente não
            propaga esses ajustes.
          </Typography>
        </Box>
      </Stack>

      {erros.length > 0 && (
        <Alert severity="error" onClose={onFecharErros}>
          {erros.join(" ")}
        </Alert>
      )}

      {!readOnly && (
        <Typography variant="caption" color="text.secondary">
          * Campos obrigatórios
        </Typography>
      )}
    </>
  );
}

type CenarioFiltro = {
  descricao?: string;
  mesMeta?: string;
  situacao?: SituacaoFiltro;
};

const FILTROS_VAZIOS = { descricao: "", mesMeta: "", situacao: "" as SituacaoFiltro | "" };

/** De quanto em quanto tempo a tela pergunta ao back se o cálculo já terminou. */
const INTERVALO_ACOMPANHAMENTO_MS = 5_000;

/**
 * Teto do acompanhamento, alinhado com a expiração do cálculo no back
 * (calculoExpiracao, cenarios.go): passado isso o back já devolveu o cenário
 * para edição, então continuar perguntando não traria nada novo.
 */
const LIMITE_ACOMPANHAMENTO_MS = 30 * 60 * 1_000;

/** Criar, alterar e excluir cenários exige o papel "Criador de Meta" (save/delete_cenario, back-end). */
export function MetasPeriodosTab({ temPapelCriador = false }: { temPapelCriador?: boolean } = {}) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const codSistema = user!.loggedClient!.system_id;
  const { notify } = useNotification();
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Cenario>();

  const mesMinimo = useMemo(mesAtual, []);

  const [cenarios, setCenarios] = useState<Cenario[]>([]);
  const [distribuicoesArea, setDistribuicoesArea] = useState<DistribuicaoArea[]>([]);
  const [erros, setErros] = useState<string[]>([]);
  const [excluindo, setExcluindo] = useState<Cenario | null>(null);
  // Editar ou recalcular um CALCULADO descarta o resultado atual (UpdateCenario/
  // MarkCenarioCalculando, back-end) — confirma antes de deixar a ação seguir.
  const [confirmando, setConfirmando] = useState<{ cenario: Cenario; acao: "editar" | "calcular" } | null>(null);
  const [loading, setLoading] = useState(true);
  const [parametros, setParametros] = useState<Cenario | null>(null);
  // PROMOVIDO e CALCULANDO abrem no mesmo modal, só para consulta — sem Salvar.
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  // Trava a linha inteira durante o cálculo, já que ele não pode ser
  // cancelado nem se sobrepor a outra ação no mesmo cenário.
  const [processando, setProcessando] = useState<{ id: string; acao: "calcular" } | null>(null);

  const [inputValues, setInputValues] = useState(FILTROS_VAZIOS);
  const [filters, setFilters] = useState<CenarioFiltro>({});

  useEffect(() => {
    // Sem limpar, os cenários do cliente anterior seguem na tela e clicáveis
    // até a resposta nova chegar — e para sempre, se ela falhar.
    setLoading(true);
    setCenarios([]);
    listCenarios(clientId)
      .then((salvos) => setCenarios(salvos.map(daApi)))
      .catch(() => notify("Não foi possível carregar os cenários.", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Falha aqui não bloqueia a tela: o cenário só fica sem opções de distribuição.
  useEffect(() => {
    setDistribuicoesArea([]);
    listDistribuicoesArea(clientId)
      .then(setDistribuicoesArea)
      .catch(() => notify("Não foi possível carregar as distribuições de área.", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Um cenário novo abre com os meses do cenário mais recente já marcados,
  // desde que ainda sejam um período histórico permitido.
  const impactosPadrao = useMemo<Impactos>(
    () => (cenarios[0] ? comPermitidos(cenarios[0].impactos) : {}),
    [cenarios]
  );

  const filtrados = useMemo(() => {
    const termo = normalizar(filters.descricao ?? "");
    return cenarios.filter((c) => {
      if (termo && !normalizar(c.descricao).includes(termo)) return false;
      if (filters.mesMeta && c.mesMeta !== filters.mesMeta) return false;
      if (filters.situacao && situacaoFiltroDe(c.status) !== filters.situacao) return false;
      return true;
    });
  }, [cenarios, filters]);

  const paginados = useMemo(
    () => filtrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtrados, page, rowsPerPage]
  );

  // Excluir os últimos registros pode deixar a página corrente vazia.
  useEffect(() => {
    if (page > 0 && paginados.length === 0) setPage(0);
  }, [page, paginados.length, setPage]);

  const abrirNovo = () => {
    setErros([]);
    setSomenteLeitura(false);
    handleCreate();
  };

  const abrirEdicao = (cenario: Cenario) => {
    // RASCUNHO/ENVIADO ainda não têm resultado a perder — só CALCULADO pede confirmação.
    if (cenario.status === "CALCULADO") {
      setConfirmando({ cenario, acao: "editar" });
      return;
    }
    editarCenario(cenario);
  };

  const editarCenario = (cenario: Cenario) => {
    setErros([]);
    setSomenteLeitura(false);
    handleEdit(cenario);
  };

  const abrirVisualizacao = (cenario: Cenario) => {
    setErros([]);
    setSomenteLeitura(true);
    handleEdit(cenario);
  };

  const onSave = async (values: Partial<Cenario>) => {
    const rascunho: Rascunho = { ...rascunhoVazio(impactosPadrao), ...values };
    const problemas = validar(rascunho, mesMinimo);
    setErros(problemas);
    if (problemas.length) return;

    let salvo: Cenario;
    try {
      // A resposta traz o id gerado no insert, então é ela que entra na lista.
      salvo = daApi(await saveCenario(paraApi(rascunho, codSistema, editing?.id), clientId));
    } catch (err: any) {
      notify(err?.message || "Não foi possível salvar o cenário.", "error");
      return;
    }

    setCenarios((prev) => (editing ? prev.map((c) => (c.id === editing.id ? salvo : c)) : [salvo, ...prev]));
    notify("Cenário gravado.", "success");
    handleClose();
  };

  /**
   * Dispara PKG_FLASH_META_CALC.CALCULAR no banco do cliente. O Method aceita o
   * disparo e responde na hora com o cenário em CALCULANDO — quem espera o
   * motor terminar é o acompanhamento logo abaixo, não esta chamada.
   */
  const dispararCalculo = async (cenario: Cenario) => {
    setProcessando({ id: cenario.id, acao: "calcular" });
    try {
      const { status } = await calcularCenarioApi(cenario.id, clientId);
      setCenarios((prev) =>
        prev.map((c) => (c.id === cenario.id ? { ...c, status, erroCalculo: undefined } : c))
      );
      notify(`Cálculo de "${cenario.descricao}" iniciado — pode levar alguns minutos.`, "info");
    } catch (err: any) {
      notify(err?.message || `Não foi possível calcular "${cenario.descricao}".`, "error");
    } finally {
      setProcessando(null);
    }
  };

  // RASCUNHO/ENVIADO ainda não têm resultado a perder — só CALCULADO (recalcular) pede confirmação.
  const calcularCenario = (cenario: Cenario) => {
    if (cenario.status === "CALCULADO") {
      setConfirmando({ cenario, acao: "calcular" });
      return;
    }
    dispararCalculo(cenario);
  };

  const onConfirmarAcao = () => {
    if (!confirmando) return;
    const { cenario, acao } = confirmando;
    setConfirmando(null);
    if (acao === "editar") editarCenario(cenario);
    else dispararCalculo(cenario);
  };

  // Todo cenário em CALCULANDO é acompanhado até o back dar o desfecho. O
  // cálculo roda no banco do cliente, fora do request que o disparou, então
  // sair da tela não o interrompe: ao voltar, a listagem traz o CALCULANDO de
  // volta e o acompanhamento recomeça daqui sozinho.
  const acompanhando = useMemo(
    () => cenarios.filter((c) => c.status === "CALCULANDO").map((c) => ({ id: c.id, descricao: c.descricao })),
    [cenarios]
  );
  const chaveAcompanhamento = acompanhando.map((c) => c.id).join(",");

  useEffect(() => {
    if (!chaveAcompanhamento) return;

    let cancelado = false;
    let timer: ReturnType<typeof setTimeout>;
    const limite = Date.now() + LIMITE_ACOMPANHAMENTO_MS;

    // Devolve true enquanto o cenário ainda estiver calculando — inclusive
    // quando a consulta falha, que é uma resposta que não chegou, não um
    // desfecho: o ciclo seguinte pergunta de novo.
    const consultar = async (alvo: { id: string; descricao: string }) => {
      let situacao;
      try {
        situacao = await statusCalculoApi(alvo.id, clientId);
      } catch {
        return true;
      }
      if (cancelado || situacao.status === "CALCULANDO") return true;

      setCenarios((prev) =>
        prev.map((c) =>
          c.id === alvo.id ? { ...c, status: situacao.status, erroCalculo: situacao.erroCalculo } : c
        )
      );
      if (situacao.erroCalculo) {
        notify(`Não foi possível calcular "${alvo.descricao}": ${situacao.erroCalculo}`, "error");
      } else {
        notify(`Cenário "${alvo.descricao}" calculado.`, "success");
      }
      return false;
    };

    const ciclo = async () => {
      const pendentes = await Promise.all(acompanhando.map(consultar));
      if (cancelado || !pendentes.some(Boolean)) return;
      if (Date.now() >= limite) {
        notify("O cálculo está demorando mais que o esperado. Recarregue a tela para ver como terminou.", "warning");
        return;
      }
      timer = setTimeout(ciclo, INTERVALO_ACOMPANHAMENTO_MS);
    };

    timer = setTimeout(ciclo, INTERVALO_ACOMPANHAMENTO_MS);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveAcompanhamento, clientId]);

  const applyFilters = () => {
    setFilters({
      descricao: inputValues.descricao || undefined,
      mesMeta: inputValues.mesMeta || undefined,
      situacao: inputValues.situacao || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setInputValues(FILTROS_VAZIOS);
    setFilters({});
    setPage(0);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const onDeleteConfirm = async () => {
    if (!excluindo) return;
    // Fecha a confirmação antes de chamar o back: se falhar, a lista fica
    // como está e o erro chega pela notificação.
    const alvo = excluindo;
    setExcluindo(null);
    try {
      await deleteCenario(alvo.id, clientId);
    } catch (err: any) {
      notify(err?.message || "Não foi possível excluir o cenário.", "error");
      return;
    }
    setCenarios((prev) => prev.filter((c) => c.id !== alvo.id));
    notify("Cenário excluído.", "success");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Cenários de cálculo da meta: meses de referência, o peso de cada um e o crescimento esperado das vendas.
          Cada cenário gravado fica salvo na configuração de cálculo.
        </Typography>
        <Tooltip title={temPapelCriador ? "" : "Sem permissão para criar um novo cenário"}>
          <span style={{ flexShrink: 0 }}>
            <Button variant="contained" startIcon={<Add />} onClick={abrirNovo} disabled={!temPapelCriador}>
              Novo cenário
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Descrição"
          size="small"
          fullWidth
          value={inputValues.descricao}
          onChange={(e) => setInputValues((prev) => ({ ...prev, descricao: e.target.value }))}
        />
        <DatePicker
          label="Mês da meta"
          views={["year", "month"]}
          format="MM/YYYY"
          value={paraDayjs(inputValues.mesMeta)}
          onChange={(data) => setInputValues((prev) => ({ ...prev, mesMeta: deDayjs(data) }))}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <TextField
          select
          label="Situação"
          size="small"
          fullWidth
          value={inputValues.situacao}
          onChange={(e) =>
            setInputValues((prev) => ({ ...prev, situacao: e.target.value as SituacaoFiltro | "" }))
          }
        >
          <MenuItem value="">Todas</MenuItem>
          {OPCOES_SITUACAO.map((opcao) => (
            <MenuItem key={opcao.value} value={opcao.value}>
              {opcao.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterModal>

      <DataTable
        data={paginados}
        columns={[
          { key: "descricao", label: "Descrição" },
          { key: "mesMeta", label: "Mês da meta", render: (c) => rotuloMesChave(c.mesMeta) },
          {
            key: "referencias",
            label: "Referências",
            render: (c) => `${Object.keys(c.impactos).length} mês(es)`,
          },
          {
            key: "crescimento",
            label: "Crescimento",
            render: (c) => textoCrescimento(c.crescimento),
          },
          {
            key: "situacao",
            label: "Situação",
            render: (c) => {
              const status = statusCalculoDe(c.status);
              const chip = <Chip size="small" variant="outlined" label={situacaoDe(status)} color={corSituacao(status)} />;
              // O cálculo falha depois que a resposta do disparo já foi
              // embora, então o motivo só chega pela listagem — e é aqui que
              // ele continua visível depois de recarregar a tela.
              return c.erroCalculo ? (
                <Tooltip title={c.erroCalculo}>
                  <span>{chip}</span>
                </Tooltip>
              ) : (
                chip
              );
            },
          },
        ]}
        total={filtrados.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(cenario) => (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Tooltip title="Parâmetros do cálculo">
              <IconButton
                onClick={() => setParametros(cenario)}
                aria-label={`Parâmetros de ${cenario.descricao}`}
              >
                <InfoOutlined />
              </IconButton>
            </Tooltip>
            {temPapelCriador && editavel(cenario.status) ? (
              <IconButton onClick={() => abrirEdicao(cenario)} color="primary" aria-label={`Editar ${cenario.descricao}`}>
                <Edit />
              </IconButton>
            ) : (
              <Tooltip
                title={
                  !temPapelCriador
                    ? "Sem permissão para alterar o cenário — visualizar somente."
                    : cenario.status === "CALCULANDO"
                    ? "Cálculo em andamento — visualizar somente."
                    : cenario.status === "PROMOVENDO"
                    ? "Publicação em andamento — visualizar somente."
                    : "Cenário publicado — visualizar somente."
                }
              >
                <IconButton
                  onClick={() => abrirVisualizacao(cenario)}
                  color="primary"
                  aria-label={`Visualizar ${cenario.descricao}`}
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
            )}
            {temPapelCriador && excluivel(cenario.status) ? (
              <IconButton onClick={() => setExcluindo(cenario)} color="error" aria-label={`Excluir ${cenario.descricao}`}>
                <Delete />
              </IconButton>
            ) : (
              <Tooltip
                title={
                  !temPapelCriador
                    ? "Sem permissão para excluir o cenário."
                    : cenario.status === "CALCULANDO"
                    ? "Cálculo em andamento — aguarde terminar."
                    : cenario.status === "PROMOVENDO"
                    ? "Publicação em andamento — aguarde terminar."
                    : "Cenário publicado — as metas já foram mescladas."
                }
              >
                <span>
                  <IconButton disabled color="error" aria-label={`Excluir ${cenario.descricao}`}>
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={!editavel(cenario.status) || processando !== null}
              startIcon={emCalculo(cenario, processando) ? <CircularProgress size={14} /> : undefined}
              onClick={() => calcularCenario(cenario)}
            >
              {emCalculo(cenario, processando) ? "Calculando..." : "Calcular"}
            </Button>
          </Stack>
        )}
      />

      {filtrados.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
          {cenarios.length === 0
            ? "Nenhum cenário gravado."
            : "Nenhum cenário encontrado para os filtros aplicados."}
        </Typography>
      )}

      {/* Montado só enquanto aberto para que cada abertura recomece do registro atual. */}
      {open && (
        <CrudModal<Cenario, Omit<CenarioFormProps, "values" | "setValues" | "readOnly">>
          open
          onClose={handleClose}
          title={
            somenteLeitura
              ? editing?.status === "CALCULANDO"
                ? "Cálculo em andamento"
                : editing?.status === "PROMOVENDO"
                ? "Publicação em andamento"
                : "Cenário publicado"
              : editing
                ? "Editar cenário"
                : "Novo cenário"
          }
          initialData={editing}
          onSubmit={onSave}
          readOnly={somenteLeitura}
          Form={CenarioForm}
          extraFormProps={{
            erros,
            onFecharErros: () => setErros([]),
            mesMinimo,
            impactosPadrao,
            distribuicoesArea,
          }}
        />
      )}

      <MetasParametrosModal cenario={parametros} clientId={clientId} onClose={() => setParametros(null)} />

      <ConfirmModal
        open={!!excluindo}
        onClose={() => setExcluindo(null)}
        onConfirm={onDeleteConfirm}
        title="Exclusão de cenário"
        text={`Tem certeza que deseja excluir ${excluindo?.descricao ?? "o registro"}?`}
        btnText="Confirmar"
      />

      <ConfirmModal
        open={!!confirmando}
        onClose={() => setConfirmando(null)}
        onConfirm={onConfirmarAcao}
        title={confirmando?.acao === "editar" ? "Editar cenário calculado" : "Recalcular cenário"}
        text={`O cenário "${confirmando?.cenario.descricao}" já foi calculado. ${
          confirmando?.acao === "editar" ? "Editar" : "Recalcular"
        } vai descartar o resultado atual, que não poderá ser recuperado. Deseja continuar?`}
        btnText={confirmando?.acao === "editar" ? "Editar" : "Recalcular"}
      />
    </Box>
  );
}
